/**
 * Promo-code normalization + attempt classification (platform-agnostic).
 *
 * Backlog #38(a). Web promo entry read, wrote, and keyed codes at five sites
 * (App.js URL-param store, the win-back gate, the stored-code auto-apply, the
 * manual apply, and the getActivePromotionCodes map builder), each doing its
 * own partial `.toUpperCase()` — or nothing. So a code pasted or campaign-
 * linked with stray whitespace ("NY26 ", "?promo_code=LINKEDIN25 ") failed as
 * if it did not exist, no auto-apply, no error, no analytics; and one code
 * split across whitespace/case variants in PostHog.
 *
 * The fix is ONE normalizer used by every reader, writer, AND the map builder,
 * so the visible input, localStorage, analytics, and Stripe checkout all agree.
 * Web-only in practice (mobile uses RevenueCat offers, no code text entry), but
 * it lives in core per CLAUDE.md §3 so the rule is pinned by golden tests and
 * the next promo entry point can't quietly re-decide it.
 */

/**
 * Canonical form of a promo code: surrounding whitespace trimmed, upper-cased.
 * Nullish / non-string input normalizes to '' — an empty attempt.
 *
 * @param {string} code
 * @returns {string}
 */
export const normalizePromoCode = (code) =>
  (typeof code === 'string' ? code : '').trim().toUpperCase();

/**
 * Classify a promo-code entry attempt against the active-code mapping, so the
 * UI and analytics agree on what happened without each call site re-deriving
 * it. Pure: no side effects, no tracking — the caller renders and emits.
 *
 * Outcomes:
 *   - 'empty'   — nothing (or only whitespace) entered. A no-op, NOT a failed
 *                 attempt: callers must not emit promo_code_failed.
 *   - 'applied' — code exists in the mapping AND carries a percent_off.
 *   - 'invalid' — code absent, OR present but not applicable (e.g. a fixed-
 *                 amount coupon with no percent_off). `failedReason`
 *                 distinguishes the two for honest telemetry.
 *
 * Note on fixed-amount coupons: option (a) filters `amount_off`-only codes out
 * of getActivePromotionCodes upstream, so in practice they never reach the
 * mapping and surface here as 'code_not_found'. The 'unknown' branch stays as
 * a faithful, defensive classification if one ever does slip through.
 *
 * @param {string} code raw user / stored / ops input
 * @param {Object<string, {percent_off?: number, id?: string}>} mapping active codes, keyed by normalized code
 * @returns {{ outcome: 'empty'|'applied'|'invalid', code: string, promo: (object|null), failedReason: ('code_not_found'|'unknown'|null) }}
 */
export const classifyPromoAttempt = (code, mapping = {}) => {
  const normalized = normalizePromoCode(code);
  if (!normalized) {
    return { outcome: 'empty', code: normalized, promo: null, failedReason: null };
  }
  const promo = (mapping && mapping[normalized]) || null;
  if (promo && promo.percent_off) {
    return { outcome: 'applied', code: normalized, promo, failedReason: null };
  }
  return {
    outcome: 'invalid',
    code: normalized,
    promo,
    failedReason: promo ? 'unknown' : 'code_not_found',
  };
};
